import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { categories, products } from '@/mocks/products';
import ProductCard from '@/components/feature/ProductCard';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function Categories() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'fashion';
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [searchParams, activeTab]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const filteredProducts = products.filter((p) => p.category === activeTab);
  const activeCategory = categories.find((c) => c.id === activeTab);

  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <section className="relative py-12 md:py-16 bg-background-100 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20elegant%20minimalist%20background%20with%20subtle%20Asian%20textile%20patterns%20in%20warm%20cream%20and%20golden%20amber%20tones%2C%20abstract%20gentle%20gradient%2C%20premium%20lifestyle%20atmosphere&width=1800&height=400&seq=category-header&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '分类浏览' }]} />
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2">分类浏览</h1>
          <p className="mt-2 text-sm text-foreground-500">按品类浏览来自亚洲各地的精选好物</p>
        </div>
      </section>

      {/* Tab Switcher */}
      <section className="bg-background-50 border-b border-background-200 sticky top-16 md:top-[72px] z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-1 py-3 overflow-x-auto">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleTabChange(cat.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeTab === cat.id
                    ? 'bg-primary-500 text-white'
                    : 'text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Category Info */}
      <section className="bg-background-50 py-6">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {activeCategory && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className={`${activeCategory.icon} text-lg`}></i>
              </div>
              <div>
                <h2 className="font-heading text-lg font-semibold text-foreground-900">{activeCategory.name}</h2>
                <p className="text-sm text-foreground-500">{activeCategory.description}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-8 md:py-12 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-product-shop>
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-200 text-foreground-400">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="mt-4 text-foreground-500">该分类暂无产品</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}